import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { cerrarSesion, iniciarSesion, obtenerPerfilActual } from '../api/sesion';
import { LocalRepo } from '../data/LocalRepo';
import { SupabaseRepo } from '../data/SupabaseRepo';
import type { AcademiaRepo } from '../data/AcademiaRepo';
import { ESPACIO_BIMBO } from '../data/contenido/bimbo';
import { aplicarTema } from '../tema/aplicarTema';
import type { Espacio, PerfilRecurso } from '../domain/types';

interface ValorSesion {
  perfil: PerfilRecurso | null;
  repo: AcademiaRepo | null;
  espacio: Espacio;
  puntos: number;
  cargando: boolean;
  entrar: (documento: string, clave: string) => Promise<void>;
  salir: () => Promise<void>;
  refrescarPuntos: () => Promise<void>;
}

const Contexto = createContext<ValorSesion | null>(null);

export function SesionProvider({ children }: { children: ReactNode }) {
  const [perfil, setPerfil] = useState<PerfilRecurso | null>(null);
  const [cargando, setCargando] = useState(true);
  const [puntos, setPuntos] = useState(0);

  const [espacio, setEspacio] = useState<Espacio>(ESPACIO_BIMBO);
  const [conBackend, setConBackend] = useState<boolean | null>(null);

  /**
   * Al entrar se prueba si el backend responde. Si el esquema `academia` todavía no está
   * expuesto en la API del proyecto, la app **no se cae**: vuelve al contenido local y el
   * aviso de la pantalla sigue diciendo que el avance no se guarda.
   *
   * Una app que muestra pantalla en blanco porque falta un ajuste de configuración es peor
   * que una que sigue funcionando en modo demostración y lo dice.
   */
  useEffect(() => {
    if (!perfil) {
      setConBackend(null);
      return;
    }
    let vivo = true;
    void new SupabaseRepo()
      .obtenerEspacio(ESPACIO_BIMBO.slug)
      .then((e) => {
        if (!vivo) return;
        if (e) {
          setEspacio(e);
          setConBackend(true);
        } else {
          setConBackend(false);
        }
      })
      .catch(() => {
        if (vivo) setConBackend(false);
      });
    return () => {
      vivo = false;
    };
  }, [perfil]);

  // El repo local se ata al id del trabajador: dos personas en el mismo celular no
  // comparten avance.
  const repo = useMemo<AcademiaRepo | null>(() => {
    if (!perfil || conBackend === null) return null;
    return conBackend ? new SupabaseRepo() : new LocalRepo(perfil.id);
  }, [perfil, conBackend]);

  useEffect(() => {
    aplicarTema(espacio.tema);
  }, [espacio]);

  useEffect(() => {
    let vivo = true;
    obtenerPerfilActual()
      .then((p) => {
        if (vivo) setPerfil(p);
      })
      .catch(() => {
        // Sin red o sesión inválida: se entra como no autenticado en vez de quedar colgado.
        if (vivo) setPerfil(null);
      })
      .finally(() => {
        if (vivo) setCargando(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const refrescarPuntos = useCallback(async () => {
    if (repo) setPuntos(await repo.obtenerPuntos());
  }, [repo]);

  useEffect(() => {
    void refrescarPuntos();
  }, [refrescarPuntos]);

  const entrar = useCallback(async (documento: string, clave: string) => {
    setPerfil(await iniciarSesion(documento, clave));
  }, []);

  const salir = useCallback(async () => {
    await cerrarSesion();
    setPerfil(null);
    setPuntos(0);
    // ⚠️ NO se borra `academia:<workerId>` del almacenamiento. La demo original hacía
    // `borrarSesion()` al salir y el avance se perdía: quien cerraba sesión volvía a cero.
  }, []);

  const valor: ValorSesion = {
    perfil,
    repo,
    espacio,
    puntos,
    cargando,
    entrar,
    salir,
    refrescarPuntos,
  };

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSesion(): ValorSesion {
  const valor = useContext(Contexto);
  if (!valor) throw new Error('useSesion se usó fuera de SesionProvider');
  return valor;
}
